import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Dialog, DialogContent, DialogActions, Button, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { RootState } from '../../../redux/store';
import axios from '../../../interceptors/axios';
import { usePageHeader } from '../../../context/PageHeaderContext';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DsScreen {
  id: number;
  device_id: string;
  company_id: string;
  display_name: string;
  last_seen_at: string | null;
  app_version: string | null;
  ip_address: string | null;
  config_version: number;
  created_at: string;
  default_content_type: string | null;
  default_playlist_id: number | null;
  default_media_id: number | null;
  default_media_scale: string | null;
  schedule_id: number | null;
  files_on_device: number[] | null;
  kiosk_mode: boolean;
  crash_log_enabled: boolean;
  disable_standby: boolean;
  autostart_on_boot: boolean;
  last_tally_at: string | null;
  screen_orientation: number;
  content_rotation: number;
  cache_ttl_days: number;
  offline_timeout_days: number;
  storage_free_bytes: number | null;
  storage_total_bytes: number | null;
}

interface Playlist { id: number; name: string; }
interface MediaFile { id: number; original_name: string; mime_type: string; stored_path: string; storage_name?: string; }
interface Schedule { id: number; name: string; }

// ── Constants ─────────────────────────────────────────────────────────────────

const ONLINE_MS = 6 * 60 * 1000;
const SCREENSHOT_INTERVAL = 30; // seconds
const THUMB_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3002/api').replace('/api', '');

const isOnline = (last: string | null) =>
  !!last && Date.now() - new Date(last).getTime() < ONLINE_MS;

const fmtDate = (d: string | null, fallback: string) =>
  d ? new Date(d).toLocaleString() : fallback;

const fmtRelative = (d: string | null) => {
  if (!d) return null;
  const diff = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// ── Styles ────────────────────────────────────────────────────────────────────

const glassSx = {
  background: 'var(--glass-bg)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid var(--glass-border)',
};

const selectSx = {
  background: 'rgba(255,255,255,.04)',
  border: '1px solid var(--glass-border)',
  borderRadius: '8px',
  px: '12px', py: '8px',
  fontSize: 13, color: 'var(--text-primary)',
  outline: 'none', cursor: 'pointer', width: '100%',
  '&:focus': { borderColor: 'var(--accent)' },
  transition: 'border-color .15s',
};

const labelSx = {
  fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const,
  letterSpacing: '.07em', color: 'var(--text-disabled)', mb: '8px',
};

// ── Icons ─────────────────────────────────────────────────────────────────────

const IcoBack = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const IcoScreen = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const IcoClose = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IcoPlay = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const IcoImage = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.4}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const IcoRefresh = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const IcoCamera = ({ size = 40 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.3} strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const IcoSettings = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

// ── MediaPickerModal ──────────────────────────────────────────────────────────

interface MediaPickerProps {
  files: MediaFile[];
  initialFileId: number | null;
  initialScale: string;
  onConfirm: (fileId: number, scale: string) => void;
  onClose: () => void;
  t: (k: string) => string;
}

const MediaPickerModal: React.FC<MediaPickerProps> = ({ files, initialFileId, initialScale, onConfirm, onClose, t }) => {
  const [selId, setSelId] = useState<number | null>(initialFileId);
  const [scale, setScale] = useState(initialScale || 'fill');
  const selFile = files.find(f => f.id === selId) || null;
  const isVid = (f: MediaFile) => f.mime_type?.startsWith('video/');
  const thumb = (f: MediaFile) => isVid(f) ? null : `${THUMB_BASE}/${f.stored_path}`;
  const scaleLabel = (s: string) => ({ fill: t('screens.detail.scaleFill'), fit: t('screens.detail.scaleFit'), stretch: t('screens.detail.scaleStretch') }[s] || s);

  return ReactDOM.createPortal(
    <Box onClick={onClose} sx={{ position: 'fixed', inset: 0, zIndex: 99000, background: 'rgba(0,0,0,.65)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box onClick={e => e.stopPropagation()} sx={{ width: '80vw', height: '80vh', borderRadius: '16px', display: 'flex', flexDirection: 'column', overflow: 'hidden', ...glassSx }}>
        <Box sx={{ px: '20px', py: '14px', borderBottom: '1px solid var(--divider)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t('screens.detail.chooseFile')}</Typography>
          <Box component="button" onClick={onClose} sx={{ ...glassSx, borderRadius: '8px', p: '6px 8px', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', '&:hover': { color: 'var(--text-primary)' } }}><IcoClose /></Box>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, overflowY: 'auto', p: '16px', borderRight: '1px solid var(--divider)' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px' }}>
              {files.map(f => {
                const sel = selId === f.id;
                const url = thumb(f);
                return (
                  <Box key={f.id} onClick={() => setSelId(f.id)} sx={{ borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', position: 'relative', border: `2px solid ${sel ? 'var(--accent)' : 'transparent'}`, transition: 'border-color .12s', bgcolor: 'rgba(255,255,255,.04)', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { borderColor: sel ? 'var(--accent)' : 'var(--glass-border)' } }}>
                    {url ? <Box component="img" src={url} alt={f.original_name} sx={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} /> : <Box sx={{ color: 'var(--text-disabled)' }}><IcoPlay /></Box>}
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, px: '6px', py: '4px', background: 'linear-gradient(transparent, rgba(0,0,0,.65))', zIndex: 2 }}>
                      <Typography sx={{ fontSize: 10, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_name}</Typography>
                    </Box>
                    {sel && <Box sx={{ position: 'absolute', top: '6px', right: '6px', width: 18, height: 18, borderRadius: '50%', bgcolor: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg></Box>}
                  </Box>
                );
              })}
            </Box>
          </Box>
          <Box sx={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', p: '20px', gap: '14px', overflowY: 'auto' }}>
            {selFile ? (
              <>
                <Box sx={{ borderRadius: '10px', overflow: 'hidden', bgcolor: 'rgba(0,0,0,.4)', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {!isVid(selFile) && thumb(selFile)
                    ? <Box component="img" src={thumb(selFile)!} alt={selFile.original_name} sx={{ width: '100%', height: '100%', objectFit: scale === 'fit' ? 'contain' : scale === 'stretch' ? 'fill' : 'cover' }} />
                    : <Box sx={{ color: 'var(--text-disabled)', textAlign: 'center' }}><IcoPlay size={24} /><Typography sx={{ fontSize: 11, mt: '4px' }}>Video</Typography></Box>}
                </Box>
                <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)', wordBreak: 'break-all' }}>{selFile.original_name}</Typography>
                <Box>
                  <Typography sx={{ ...labelSx }}>{t('screens.detail.scale')}</Typography>
                  {(['fill', 'fit', 'stretch'] as const).map(s => (
                    <Box key={s} onClick={() => setScale(s)} sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '6px', px: '12px', py: '7px', borderRadius: '8px', cursor: 'pointer', border: `1px solid ${scale === s ? 'var(--accent)' : 'var(--glass-border)'}`, bgcolor: scale === s ? 'rgba(99,102,241,.1)' : 'rgba(255,255,255,.03)', transition: 'all .12s' }}>
                      <Box sx={{ width: 13, height: 13, borderRadius: '50%', border: `2px solid ${scale === s ? 'var(--accent)' : 'var(--text-disabled)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {scale === s && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'var(--accent)' }} />}
                      </Box>
                      <Typography sx={{ fontSize: 13, color: scale === s ? 'var(--accent)' : 'var(--text-primary)' }}>{scaleLabel(s)}</Typography>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-disabled)', textAlign: 'center' }}>
                <Box><IcoImage size={28} /><Typography sx={{ fontSize: 12, mt: '8px' }}>Select a file</Typography></Box>
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ px: '20px', py: '12px', borderTop: '1px solid var(--divider)', display: 'flex', justifyContent: 'flex-end', gap: '10px', flexShrink: 0 }}>
          <Box component="button" onClick={onClose} sx={{ px: '14px', py: '7px', borderRadius: '8px', ...glassSx, color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>Cancel</Box>
          <Box component="button" disabled={!selId} onClick={() => selId && onConfirm(selId, scale)} sx={{ px: '14px', py: '7px', borderRadius: '8px', fontSize: 13, fontWeight: 600, cursor: selId ? 'pointer' : 'default', bgcolor: selId ? 'var(--accent)' : 'rgba(99,102,241,.3)', border: '1px solid transparent', color: '#fff', opacity: selId ? 1 : .5, transition: 'opacity .15s' }}>Select</Box>
        </Box>
      </Box>
    </Box>,
    document.body,
  );
};

// ── SettingsTab ───────────────────────────────────────────────────────────────

interface SettingsTabProps {
  screen: DsScreen;
  playlists: Playlist[];
  mediaFiles: MediaFile[];
  schedules: Schedule[];
  t: (k: string) => string;
  // controlled form state lifted to parent
  name: string; setName: (v: string) => void;
  contentType: string; setContentType: (v: string) => void;
  playlistId: number | null; setPlaylistId: (v: number | null) => void;
  mediaId: number | null; setMediaId: (v: number | null) => void;
  mediaScale: string; setMediaScale: (v: string) => void;
  scheduleId: number | null; setScheduleId: (v: number | null) => void;
  kioskMode: boolean; setKioskMode: (v: boolean) => void;
  crashLogEnabled: boolean; setCrashLogEnabled: (v: boolean) => void;
  disableStandby: boolean; setDisableStandby: (v: boolean) => void;
  autostart: boolean; setAutostart: (v: boolean) => void;
  screenOrientation: number; setScreenOrientation: (v: number) => void;
  contentRotation: number; setContentRotation: (v: number) => void;
  cacheTtlDays: number; setCacheTtlDays: (v: number) => void;
  playlist10bit?: { fileName: string; mediaFileId: number }[];
}

const ROTATION_OPTIONS = [0, 1, 2, 3];
const rotationLabel = (i: number) => `${i * 90}°`;

const SettingsTab: React.FC<SettingsTabProps> = ({
  screen, playlists, mediaFiles, schedules, t,
  name, setName, contentType, setContentType,
  playlistId, setPlaylistId, mediaId, setMediaId,
  mediaScale, setMediaScale, scheduleId, setScheduleId,
  kioskMode, setKioskMode, crashLogEnabled, setCrashLogEnabled,
  disableStandby, setDisableStandby,
  autostart, setAutostart,
  screenOrientation, setScreenOrientation, contentRotation, setContentRotation,
  cacheTtlDays, setCacheTtlDays, playlist10bit = [],
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  const selMedia = mediaFiles.find(f => f.id === mediaId) || null;
  const isVid = (f: MediaFile) => f.mime_type?.startsWith('video/');
  const scaleLabel = (s: string) => ({ fill: t('screens.detail.scaleFill'), fit: t('screens.detail.scaleFit'), stretch: t('screens.detail.scaleStretch') }[s] || s);

  const rowSx = { display: 'flex', flexDirection: 'column' as const, gap: '8px', py: '14px', borderBottom: '1px solid var(--divider)', '&:last-child': { borderBottom: 'none', pb: 0 } };

  return (
    <Box sx={{ p: '2px 4px' }}>
      <Box sx={{ borderRadius: '10px', bgcolor: 'var(--glass-hover)', border: '1px solid var(--glass-border)', px: '16px' }}>

        {/* Device name */}
        <Box sx={rowSx}>
          <Typography sx={labelSx}>{t('screens.displayName')}</Typography>
          <Box component="input" value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            sx={{ flex: 1, background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '8px', px: '12px', py: '7px', fontSize: 13, color: 'var(--text-primary)', outline: 'none', '&:focus': { borderColor: 'var(--accent)' }, transition: 'border-color .15s' }}
            placeholder="Screen name…"
          />
        </Box>

        {/* Schedule */}
        <Box sx={rowSx}>
          <Typography sx={labelSx}>{t('screens.detail.scheduledContent')}</Typography>
          <Box component="select" value={scheduleId ?? ''} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setScheduleId(e.target.value ? +e.target.value : null)} sx={selectSx}>
            <option value="">{t('screens.detail.noSchedule')}</option>
            {schedules.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Box>
        </Box>

        {/* Default Content */}
        <Box sx={rowSx}>
          <Typography sx={labelSx}>{t('screens.detail.defaultContent')}</Typography>
          <Box sx={{ display: 'flex', gap: '6px' }}>
            {([['off', t('screens.detail.contentOff')], ['playlist', t('screens.detail.contentPlaylist')], ['media', t('screens.detail.contentMedia')]] as [string, string][]).map(([val, label]) => (
              <Box key={val} onClick={() => setContentType(val)} sx={{ px: '12px', py: '5px', borderRadius: '20px', cursor: 'pointer', fontSize: 12, fontWeight: 600, border: `1px solid ${contentType === val ? 'var(--accent)' : 'var(--glass-border)'}`, bgcolor: contentType === val ? 'rgba(99,102,241,.15)' : 'transparent', color: contentType === val ? 'var(--accent)' : 'var(--text-secondary)', transition: 'all .12s' }}>{label}</Box>
            ))}
          </Box>
          {contentType === 'playlist' && (
            <>
              <Box component="select" value={playlistId ?? ''} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPlaylistId(e.target.value ? +e.target.value : null)} sx={selectSx}>
                <option value="">{t('screens.detail.noPlaylists')}</option>
                {playlists.map(pl => <option key={pl.id} value={pl.id}>{pl.name}</option>)}
              </Box>
              {playlist10bit.length > 0 && (
                <Box sx={{ px: '12px', py: '8px', borderRadius: '8px', bgcolor: 'rgba(255,170,0,.08)', border: '1px solid rgba(255,170,0,.25)', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <Box component="span" sx={{ flexShrink: 0, width: 16, height: 16, borderRadius: '50%', bgcolor: 'rgba(255,170,0,.25)', color: '#ffaa00', fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mt: '1px' }}>!</Box>
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#ffaa00', mb: '3px' }}>{t('screens.bitDepthWarnTitle')}</Typography>
                    <Typography sx={{ fontSize: 10, color: 'rgba(255,170,0,.6)', fontFamily: 'monospace' }}>{playlist10bit.map(f => f.fileName).join(', ')}</Typography>
                  </Box>
                </Box>
              )}
            </>
          )}
          {contentType === 'media' && (
            <>
              <Box component="button" onClick={() => setPickerOpen(true)} sx={{ width: '100%', textAlign: 'left', px: '12px', py: '9px', background: 'var(--input-bg)', border: `1px solid ${selMedia ? 'var(--accent)' : 'var(--input-border)'}`, borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', boxSizing: 'border-box' as const, '&:hover': { borderColor: 'var(--accent)' }, transition: 'border-color .15s' }}>
                {selMedia ? (
                  <>
                    {!isVid(selMedia)
                      ? <Box component="img" src={`${THUMB_BASE}/${selMedia.stored_path}`} alt="" sx={{ width: 32, height: 32, borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }} />
                      : <Box sx={{ width: 32, height: 32, borderRadius: '6px', bgcolor: 'rgba(99,102,241,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--accent)' }}><IcoPlay /></Box>}
                    <Typography sx={{ fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{selMedia.original_name}</Typography>
                  </>
                ) : <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>{t('screens.detail.chooseFile')}</Typography>}
              </Box>
              {selMedia && (
                <Box sx={{ display: 'flex', gap: '6px' }}>
                  {(['fill', 'fit', 'stretch'] as const).map(s => (
                    <Box key={s} onClick={() => setMediaScale(s)} sx={{ flex: 1, textAlign: 'center', py: '4px', borderRadius: '6px', cursor: 'pointer', fontSize: 11, fontWeight: 600, border: `1px solid ${mediaScale === s ? 'var(--accent)' : 'var(--glass-border)'}`, bgcolor: mediaScale === s ? 'rgba(99,102,241,.12)' : 'transparent', color: mediaScale === s ? 'var(--accent)' : 'var(--text-secondary)', transition: 'all .12s' }}>
                      {scaleLabel(s)}
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Autostart on boot */}
        <Box sx={{ ...rowSx, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ ...labelSx, mb: '2px' }}>{t('screens.detail.autostart')}</Typography>
            <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{t('screens.detail.autostartHint')}</Typography>
          </Box>
          <Box onClick={() => setAutostart(!autostart)} sx={{ position: 'relative', width: 42, height: 24, borderRadius: '12px', cursor: 'pointer', flexShrink: 0, ml: '16px', bgcolor: autostart ? 'var(--accent)' : 'rgba(255,255,255,.12)', border: `1px solid ${autostart ? 'var(--accent)' : 'var(--glass-border)'}`, transition: 'background-color .2s, border-color .2s', boxShadow: autostart ? '0 0 8px var(--accent-glow)' : 'none' }}>
            <Box sx={{ position: 'absolute', top: '2px', left: autostart ? '18px' : '2px', width: 18, height: 18, borderRadius: '50%', bgcolor: '#fff', transition: 'left .2s', boxShadow: '0 1px 4px rgba(0,0,0,.3)' }} />
          </Box>
        </Box>

        {/* Kiosk mode */}
        <Box sx={{ ...rowSx, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ ...labelSx, mb: '2px' }}>{t('screens.detail.kioskMode')}</Typography>
            <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{t('screens.detail.kioskModeHint')}</Typography>
          </Box>
          <Box onClick={() => setKioskMode(!kioskMode)} sx={{ position: 'relative', width: 42, height: 24, borderRadius: '12px', cursor: 'pointer', flexShrink: 0, ml: '16px', bgcolor: kioskMode ? 'var(--accent)' : 'rgba(255,255,255,.12)', border: `1px solid ${kioskMode ? 'var(--accent)' : 'var(--glass-border)'}`, transition: 'background-color .2s, border-color .2s', boxShadow: kioskMode ? '0 0 8px var(--accent-glow)' : 'none' }}>
            <Box sx={{ position: 'absolute', top: '2px', left: kioskMode ? '18px' : '2px', width: 18, height: 18, borderRadius: '50%', bgcolor: '#fff', transition: 'left .2s', boxShadow: '0 1px 4px rgba(0,0,0,.3)' }} />
          </Box>
        </Box>

        {/* Crash log reporting */}
        <Box sx={{ ...rowSx, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ ...labelSx, mb: '2px' }}>{t('screens.detail.crashLogEnabled')}</Typography>
            <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{t('screens.detail.crashLogEnabledHint')}</Typography>
          </Box>
          <Box onClick={() => setCrashLogEnabled(!crashLogEnabled)} sx={{ position: 'relative', width: 42, height: 24, borderRadius: '12px', cursor: 'pointer', flexShrink: 0, ml: '16px', bgcolor: crashLogEnabled ? 'var(--accent)' : 'rgba(255,255,255,.12)', border: `1px solid ${crashLogEnabled ? 'var(--accent)' : 'var(--glass-border)'}`, transition: 'background-color .2s, border-color .2s', boxShadow: crashLogEnabled ? '0 0 8px var(--accent-glow)' : 'none' }}>
            <Box sx={{ position: 'absolute', top: '2px', left: crashLogEnabled ? '18px' : '2px', width: 18, height: 18, borderRadius: '50%', bgcolor: '#fff', transition: 'left .2s', boxShadow: '0 1px 4px rgba(0,0,0,.3)' }} />
          </Box>
        </Box>

        {/* Disable standby */}
        <Box sx={{ ...rowSx, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ ...labelSx, mb: '2px' }}>{t('screens.detail.disableStandby')}</Typography>
            <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{t('screens.detail.disableStandbyHint')}</Typography>
          </Box>
          <Box onClick={() => setDisableStandby(!disableStandby)} sx={{ position: 'relative', width: 42, height: 24, borderRadius: '12px', cursor: 'pointer', flexShrink: 0, ml: '16px', bgcolor: disableStandby ? 'var(--accent)' : 'rgba(255,255,255,.12)', border: `1px solid ${disableStandby ? 'var(--accent)' : 'var(--glass-border)'}`, transition: 'background-color .2s, border-color .2s', boxShadow: disableStandby ? '0 0 8px var(--accent-glow)' : 'none' }}>
            <Box sx={{ position: 'absolute', top: '2px', left: disableStandby ? '18px' : '2px', width: 18, height: 18, borderRadius: '50%', bgcolor: '#fff', transition: 'left .2s', boxShadow: '0 1px 4px rgba(0,0,0,.3)' }} />
          </Box>
        </Box>

        {/* Screen orientation */}
        <Box sx={{ ...rowSx }}>
          <Typography sx={{ ...labelSx, mb: '2px' }}>{t('screens.detail.screenOrientation')}</Typography>
          <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', mb: '8px' }}>{t('screens.detail.screenOrientationHint')}</Typography>
          <Box sx={{ display: 'flex', gap: '6px' }}>
            {ROTATION_OPTIONS.map(i => (
              <Box key={i} component="button" onClick={() => setScreenOrientation(i)}
                sx={{ flex: 1, py: '8px', borderRadius: '8px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit', transition: 'all .15s',
                  bgcolor: screenOrientation === i ? 'var(--selected-bg)' : 'var(--glass-bg)',
                  border: `1px solid ${screenOrientation === i ? 'var(--accent)' : 'var(--glass-border)'}`,
                  color: screenOrientation === i ? 'var(--accent-light)' : 'var(--text-secondary)' }}>
                {rotationLabel(i)}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Content rotation */}
        <Box sx={{ ...rowSx }}>
          <Typography sx={{ ...labelSx, mb: '2px' }}>{t('screens.detail.contentRotation')}</Typography>
          <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', mb: '8px' }}>{t('screens.detail.contentRotationHint')}</Typography>
          <Box sx={{ display: 'flex', gap: '6px' }}>
            {ROTATION_OPTIONS.map(i => (
              <Box key={i} component="button" onClick={() => setContentRotation(i)}
                sx={{ flex: 1, py: '8px', borderRadius: '8px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit', transition: 'all .15s',
                  bgcolor: contentRotation === i ? 'var(--selected-bg)' : 'var(--glass-bg)',
                  border: `1px solid ${contentRotation === i ? 'var(--accent)' : 'var(--glass-border)'}`,
                  color: contentRotation === i ? 'var(--accent-light)' : 'var(--text-secondary)' }}>
                {rotationLabel(i)}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Cache TTL */}
        <Box sx={{ ...rowSx, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ ...labelSx, mb: '2px' }}>{t('screens.cacheTtlDays')}</Typography>
            <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{t('screens.cacheTtlHint')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, ml: '16px' }}>
            <Box component="input" type="number" min={0} max={365} value={cacheTtlDays}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCacheTtlDays(Math.max(0, +e.target.value))}
              sx={{ width: 60, background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: '8px', px: '8px', py: '5px', fontSize: 13, color: 'var(--text-primary)', outline: 'none', textAlign: 'center', fontFamily: 'inherit', '&:focus': { borderColor: 'var(--accent)' } }}
            />
            <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>
              {cacheTtlDays === 0 ? t('screens.cacheTtlForever') : t('screens.cacheTtlDaysUnit')}
            </Typography>
          </Box>
        </Box>

      </Box>

      {pickerOpen && (
        <MediaPickerModal files={mediaFiles} initialFileId={mediaId} initialScale={mediaScale}
          onConfirm={(fid, sc) => { setMediaId(fid); setMediaScale(sc); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)} t={t}
        />
      )}
    </Box>
  );
};

// ── DeviceControlTab ──────────────────────────────────────────────────────────

const DeviceControlTab: React.FC<{ screen: DsScreen; pendingCmds: ScreenCommand[]; t: (k: string) => string; onDelete?: () => void }> = ({ screen, pendingCmds, t, onDelete }) => {
  const [autostartOn, setAutostartOn] = useState(true);
  const [volumeVal, setVolumeVal] = useState(50);
  const [ctrlSending, setCtrlSending] = useState<string | null>(null);
  const [ctrlToast, setCtrlToast] = useState<string | null>(null);
  const [apkInfo, setApkInfo] = useState<{ version_name: string; version_code: number } | null>(null);

  useEffect(() => {
    axios.get('/screens/apk-version')
      .then(r => setApkInfo(r.data))
      .catch(() => {});
  }, []);

  const sendCmd = async (command: string, payload?: Record<string, unknown>) => {
    setCtrlSending(command);
    try {
      await axios.post(`/screens/devices/${screen.id}/commands`, { command, payload });
      setCtrlToast(t('screens.cmdSent'));
      setTimeout(() => setCtrlToast(null), 3000);
    } catch {
      setCtrlToast('Error sending command');
      setTimeout(() => setCtrlToast(null), 3000);
    } finally {
      setCtrlSending(null);
    }
  };

  const sendInstall = async (mode: 'silent' | 'dialog') => {
    const key = `install_${mode}`;
    setCtrlSending(key);
    try {
      const staticBase = (axios.defaults.baseURL ?? '').replace(/\/api\/?$/, '');
      await axios.post(`/screens/devices/${screen.id}/commands`, { command: 'set_update_mode', payload: { mode } });
      if (apkInfo) {
        await axios.post(`/screens/devices/${screen.id}/commands`, { command: 'update_app', payload: {
          version_name: apkInfo.version_name,
          version_code: apkInfo.version_code,
          download_url: `${staticBase}/uploads/apk/promovideo.apk`,
        }});
      }
      setCtrlToast(t('screens.cmdSent'));
      setTimeout(() => setCtrlToast(null), 3000);
    } catch {
      setCtrlToast('Error sending command');
      setTimeout(() => setCtrlToast(null), 3000);
    } finally {
      setCtrlSending(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

      {/* Device Control */}
      <Box sx={{ p: '16px 18px', borderRadius: '10px', bgcolor: 'var(--glass-hover)', border: '1px solid var(--glass-border)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '14px' }}>
          <Typography sx={labelSx}>{t('screens.deviceControl')}</Typography>
          <Box
            component="a"
            href={`${(axios.defaults.baseURL ?? '').replace(/\/api\/?$/, '')}/uploads/apk/promovideo.apk`}
            download="promovideo.apk"
            sx={{
              display: 'flex', alignItems: 'center', gap: '5px',
              px: '10px', py: '4px', borderRadius: '7px', textDecoration: 'none',
              fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
              bgcolor: 'var(--glass-bg)', border: '1px solid var(--glass-border)',
              transition: 'all .15s',
              '&:hover': { color: 'var(--accent)', borderColor: 'var(--accent)' },
            }}
          >
            <span style={{ fontSize: 12 }}>⬇</span>
            {t('screens.downloadApk')}
          </Box>
        </Box>

        {/* Volume slider */}
        <Box sx={{ pb: '14px', mb: '14px', borderBottom: '1px solid var(--divider)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '10px' }}>
            <Typography sx={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{t('screens.volume')}</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)', fontVariantNumeric: 'tabular-nums', minWidth: 36, textAlign: 'right' }}>{volumeVal}%</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box
              component="input" type="range" min={0} max={100} value={volumeVal}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVolumeVal(+e.target.value)}
              sx={{ flex: 1, cursor: 'pointer', accentColor: 'var(--accent)', height: '4px' }}
            />
            <Box
              component="button"
              onClick={() => sendCmd('set_volume', { level: volumeVal })}
              disabled={ctrlSending === 'set_volume'}
              sx={{
                px: '12px', py: '5px', borderRadius: '7px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                cursor: ctrlSending === 'set_volume' ? 'default' : 'pointer',
                bgcolor: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)',
                transition: 'all .15s', opacity: ctrlSending === 'set_volume' ? .5 : 1,
                '&:hover': { color: 'var(--text-primary)', borderColor: 'var(--accent)' },
              }}
            >
              {ctrlSending === 'set_volume' ? '…' : t('screens.volumeSet')}
            </Box>
          </Box>
        </Box>

        {/* Action buttons */}
        {(() => {
          const staticBase = (axios.defaults.baseURL ?? '').replace(/\/api\/?$/, '');
          type Btn = { cmd: string; label: string; icon: string; danger?: boolean; indigo?: boolean; amber?: boolean; onClick?: () => void; isDisabled?: boolean };
          const btns: Btn[] = [
            { cmd: 'screen_wake',   label: t('screens.wakeScreen'),   icon: '☀' },
            { cmd: 'screen_off',    label: t('screens.screenOff'),    icon: '⏾' },
            { cmd: 'restart_app',   label: t('screens.restartApp'),   icon: '↺' },
            { cmd: 'sync_ntp',      label: t('screens.syncNtp'),      icon: '⏱' },
            {
              cmd: 'update_app',
              label: apkInfo ? `${t('screens.downloadUpdate')} v${apkInfo.version_name}` : t('screens.fetchingVer'),
              icon: '⬇',
              isDisabled: !apkInfo,
              onClick: () => { if (apkInfo) sendCmd('update_app', { version_name: apkInfo.version_name, version_code: apkInfo.version_code, download_url: `${staticBase}/uploads/apk/promovideo.apk` }); },
            },
            { cmd: 'install_silent', label: t('screens.installSilent'), icon: '🔇', indigo: true, isDisabled: !apkInfo, onClick: () => sendInstall('silent') },
            { cmd: 'install_dialog', label: t('screens.installLoud'),   icon: '🔔', amber: true,  isDisabled: !apkInfo, onClick: () => sendInstall('dialog') },
            { cmd: 'reboot_device', label: t('screens.rebootDevice'),  icon: '⏻', danger: true },
          ];
          return (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {btns.map(({ cmd, label, icon, danger, indigo, amber, onClick, isDisabled }) => {
                const loading = ctrlSending === cmd;
                const disabled = isDisabled || loading;
                const handleClick = onClick ?? (() => sendCmd(cmd));
                const colorSx = danger
                  ? { bgcolor: 'rgba(248,113,113,.08)', border: '1px solid rgba(248,113,113,.2)', color: '#f87171', '&:hover': { bgcolor: 'rgba(248,113,113,.16)' } }
                  : indigo
                  ? { bgcolor: 'rgba(99,102,241,.08)', border: '1px solid rgba(99,102,241,.2)', color: '#818cf8', '&:hover:not(:disabled)': { bgcolor: 'rgba(99,102,241,.15)' } }
                  : amber
                  ? { bgcolor: 'rgba(251,191,36,.08)', border: '1px solid rgba(251,191,36,.2)', color: '#fbbf24', '&:hover:not(:disabled)': { bgcolor: 'rgba(251,191,36,.15)' } }
                  : { bgcolor: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)', '&:hover:not(:disabled)': { color: 'var(--text-primary)', borderColor: 'var(--accent)' } };
                return (
                  <Box key={cmd} component="button" onClick={handleClick} disabled={disabled}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      py: '8px', px: '10px', borderRadius: '8px', cursor: disabled ? 'default' : 'pointer',
                      fontSize: 12, fontWeight: 600, transition: 'all .15s', fontFamily: 'inherit',
                      opacity: disabled ? .5 : 1, ...colorSx,
                    }}>
                    <span style={{ fontSize: 13 }}>{icon}</span>
                    {loading ? '…' : label}
                  </Box>
                );
              })}
            </Box>
          );
        })()}

        {/* Toast */}
        {ctrlToast && ReactDOM.createPortal(
          <Box sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, px: '18px', py: '9px', borderRadius: '10px', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(20px)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, boxShadow: '0 8px 32px rgba(0,0,0,.3)', whiteSpace: 'nowrap' }}>
            {ctrlToast}
          </Box>,
          document.body
        )}
      </Box>

      {/* Command Queue */}
      <Box sx={{ p: '16px 18px', borderRadius: '10px', bgcolor: 'var(--glass-hover)', border: '1px solid var(--glass-border)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: pendingCmds.length > 0 ? '12px' : 0 }}>
          <Typography sx={labelSx}>{t('screens.cmdQueue')}</Typography>
          {pendingCmds.length > 0 && (
            <Box sx={{ ml: '8px', px: '7px', py: '1px', borderRadius: '20px', fontSize: 10, fontWeight: 700, bgcolor: 'rgba(251,191,36,.12)', color: '#fbbf24' }}>
              {pendingCmds.length}
            </Box>
          )}
        </Box>
        {pendingCmds.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>{t('screens.noPendingCmds')}</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            {pendingCmds.map(cmd => {
              const cfg = CMD_STATUS_CFG[cmd.status] || CMD_STATUS_CFG.pending;
              const dt = new Date(cmd.created_at);
              const fmtDt = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              return (
                <Box key={cmd.id} sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '12px', py: '7px', borderRadius: '8px', bgcolor: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
                  <Box sx={{ px: '7px', py: '2px', borderRadius: '5px', fontSize: 10, fontWeight: 700, color: cfg.color, bgcolor: cfg.bg, flexShrink: 0 }}>
                    {cmd.status}
                  </Box>
                  <Typography sx={{ fontSize: 12, color: 'var(--accent)', fontFamily: '"JetBrains Mono", monospace', flex: 1 }}>{cmd.command}</Typography>
                  <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace', flexShrink: 0 }}>{fmtDt}</Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Delete — mobile only: the desktop side panel already carries it */}
      {onDelete && (
        <Box sx={{ display: { xs: 'block', md: 'none' }, pt: '14px', borderTop: '1px solid var(--divider)' }}>
          <Box component="button" onClick={onDelete}
            sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '12px', py: '6px', borderRadius: '8px', fontFamily: 'inherit',
              background: 'rgba(248,113,113,.08)', border: '1px solid rgba(248,113,113,.25)', color: '#f87171',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', '&:hover': { background: 'rgba(248,113,113,.18)' } }}>
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
            {t('screens.deleteDevice')}
          </Box>
        </Box>
      )}

    </Box>
  );
};


// ── CommandsTab ───────────────────────────────────────────────────────────────

interface ScreenCommand {
  id: number;
  device_id: string;
  command: string;
  payload: { changes?: Record<string, { from: unknown; to: unknown }> } | null;
  status: string;
  created_at: string;
  sent_at: string | null;
  executed_at: string | null;
  result: string | null;
}

const CMD_STATUS_CFG: Record<string, { color: string; bg: string }> = {
  pending:  { color: '#fbbf24', bg: 'rgba(251,191,36,.12)'  },
  sent:     { color: '#60a5fa', bg: 'rgba(96,165,250,.12)'  },
  done:     { color: '#34d399', bg: 'rgba(52,211,153,.12)'  },
  error:    { color: '#f87171', bg: 'rgba(248,113,113,.12)' },
};

const CommandsTab: React.FC<{ screen: DsScreen; t: (k: string) => string }> = ({ screen }) => {
  const [cmds, setCmds]       = useState<ScreenCommand[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchCmds = useCallback(async () => {
    try {
      const { data } = await axios.get<ScreenCommand[]>(`/screens/devices/${screen.id}/commands`, { params: { limit: 100 } });
      setCmds(Array.isArray(data) ? data : []);
    } catch { } finally { setLoading(false); }
  }, [screen.id]);

  useEffect(() => { setLoading(true); fetchCmds(); }, [fetchCmds]);

  const fmtDt = (d: string | null) =>
    d ? new Date(d).toLocaleString([], { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';

  const renderChanges = (payload: ScreenCommand['payload']) => {
    const changes = payload?.changes;
    if (!changes || Object.keys(changes).length === 0) return <span style={{ color: 'var(--text-disabled)' }}>—</span>;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {Object.entries(changes).map(([field, { from, to }]) => (
          <Box key={field} sx={{ fontSize: 10, fontFamily: '"JetBrains Mono", monospace', lineHeight: 1.5 }}>
            <span style={{ color: 'var(--text-disabled)' }}>{field}: </span>
            <span style={{ color: '#f87171' }}>{String(from ?? 'null')}</span>
            <span style={{ color: 'var(--text-disabled)' }}> → </span>
            <span style={{ color: '#34d399' }}>{String(to ?? 'null')}</span>
          </Box>
        ))}
      </Box>
    );
  };

  const tdStyle: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,.04)', verticalAlign: 'top', fontSize: 11 };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {cmds.length} command{cmds.length !== 1 ? 's' : ''}
        </Typography>
        <Box component="button" onClick={() => { setLoading(true); fetchCmds(); }} sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '4px', px: '8px', py: '4px', borderRadius: '7px', ...glassSx, color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer', '&:hover': { color: 'var(--text-primary)' } }}>
          <IcoRefresh size={11} /> Refresh
        </Box>
      </Box>

      {loading && cmds.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: '40px' }}>
          <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid var(--glass-border)', borderTopColor: 'var(--accent)', animation: 'spin .7s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
        </Box>
      ) : cmds.length === 0 ? (
        <Box sx={{ py: '40px', textAlign: 'center', color: 'var(--text-disabled)', fontSize: 13 }}>No commands yet</Box>
      ) : (
        <Box sx={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid var(--glass-border)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: '"JetBrains Mono", "Courier New", monospace' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,.03)' }}>
                {['Date', 'Command', 'Status', 'Changes', 'Result'].map(h => (
                  <th key={h} style={{ ...tdStyle, color: 'var(--text-disabled)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', fontSize: 10, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cmds.map(cmd => {
                const cfg = CMD_STATUS_CFG[cmd.status] || { color: 'var(--text-disabled)', bg: 'rgba(255,255,255,.04)' };
                return (
                  <tr key={cmd.id} style={{ transition: 'background .1s' }}>
                    <td style={{ ...tdStyle, color: 'var(--text-disabled)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDt(cmd.created_at)}</td>
                    <td style={{ ...tdStyle, color: 'var(--accent)', whiteSpace: 'nowrap' }}>{cmd.command}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'inline-flex', px: '7px', py: '2px', borderRadius: '5px', fontSize: 10, fontWeight: 700, color: cfg.color, bgcolor: cfg.bg, letterSpacing: '.04em' }}>
                        {cmd.status}
                      </Box>
                    </td>
                    <td style={{ ...tdStyle, maxWidth: 260 }}>{renderChanges(cmd.payload)}</td>
                    <td style={{ ...tdStyle, color: cmd.status === 'error' ? '#f87171' : 'var(--text-secondary)', maxWidth: 180, wordBreak: 'break-all' }}>
                      {cmd.result || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  );
};

// ── FilesOnDeviceTab ──────────────────────────────────────────────────────────

interface CachedFile {
  id: number;
  original_name: string;
  storage_name: string;
  size_bytes: number;
  mime_type: string;
  in_use: boolean;
}

interface RequiredFile {
  id: number;
  file_name: string;
  storage_name: string;
  mime_type: string;
  size_bytes: number;
  md5: string | null;
  url: string;
  sources?: ('default' | 'schedule')[];
}

const FilesOnDeviceTab: React.FC<{
  screen: DsScreen;
  playlists: Playlist[];
  schedules: Schedule[];
  mediaFiles: MediaFile[];
  t: (k: string) => string;
}> = ({ screen, playlists, schedules, mediaFiles, t }) => {
  const [files, setFiles] = useState<CachedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [required, setRequired] = useState<RequiredFile[]>([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pendingCmd, setPendingCmd] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get<CachedFile[]>(`/screens/devices/${screen.id}/cached-files`);
      setFiles(data);
    } catch { }
    finally { setLoading(false); }
  }, [screen.id]);

  const fetchRequired = useCallback(async () => {
    setReqLoading(true);
    try {
      const { data } = await axios.get<{ required_files: RequiredFile[] }>(`/screens/devices/${screen.id}/required-files`);
      setRequired(data.required_files || []);
    } catch { }
    finally { setReqLoading(false); }
  }, [screen.id]);

  useEffect(() => { fetchFiles(); fetchRequired(); }, [fetchFiles, fetchRequired]);

  const sendCommand = async (command: string, payload?: Record<string, unknown>, fileStorageName?: string) => {
    if (fileStorageName) setPendingCmd(p => new Set(p).add(fileStorageName));
    try {
      await axios.post(`/screens/devices/${screen.id}/commands`, { command, payload });
      showToast(t('screens.cmdSent'));
      if (command === 'clear_cache') { setFiles([]); setSelected(new Set()); }
      else if (fileStorageName) { setFiles(prev => prev.filter(f => f.storage_name !== fileStorageName)); setSelected(prev => { const s = new Set(prev); s.delete(fileStorageName); return s; }); }
    } catch { showToast('Error sending command'); }
    finally { if (fileStorageName) setPendingCmd(p => { const s = new Set(p); s.delete(fileStorageName); return s; }); }
  };

  const deleteSelected = async () => {
    for (const sn of Array.from(selected)) {
      const f = files.find(x => x.storage_name === sn);
      if (f) await sendCommand('delete_file', { file_id: f.id, storage_name: sn }, sn);
    }
  };

  const deleteUnused = async () => {
    for (const f of files.filter(x => !x.in_use)) {
      await sendCommand('delete_file', { file_id: f.id, storage_name: f.storage_name }, f.storage_name);
    }
  };

  const toggleSelect = (sn: string) => setSelected(prev => { const s = new Set(prev); s.has(sn) ? s.delete(sn) : s.add(sn); return s; });
  const toggleAll = () => setSelected(selected.size === files.length ? new Set() : new Set(files.map(f => f.storage_name)));

  const totalBytes = files.reduce((s, f) => s + f.size_bytes, 0);
  const reqTotalBytes = required.reduce((s, f) => s + (f.size_bytes || 0), 0);
  const fmtSize = (b: number) => b >= 1_048_576 ? `${(b / 1_048_576).toFixed(1)} MB` : b >= 1024 ? `${(b / 1024).toFixed(0)} KB` : `${b} B`;
  const isVideo = (mime: string) => mime?.startsWith('video/');

  const scheduleLabel = screen.schedule_id
    ? (schedules.find(s => s.id === screen.schedule_id)?.name ?? `Schedule #${screen.schedule_id}`)
    : null;
  const playlistLabel = screen.default_playlist_id
    ? (playlists.find(p => p.id === screen.default_playlist_id)?.name ?? `Playlist #${screen.default_playlist_id}`)
    : null;
  const mediaLabel = screen.default_media_id
    ? (mediaFiles.find(f => f.id === screen.default_media_id)?.original_name ?? 'Media file')
    : null;

  const scheduleFiles = required.filter(f => f.sources?.includes('schedule'));
  const defaultOnlyFiles = required.filter(f => f.sources?.includes('default') && !f.sources?.includes('schedule'));
  const allRequiredCount = required.length;

  const fileIcon = (mime: string) => isVideo(mime)
    ? <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
    : <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;

  const panelHeader = (title: string, count: number, bytes: number, extra?: React.ReactNode) => (
    <Box sx={{ px: '12px', py: '8px', borderBottom: '1px solid var(--glass-border)', background: 'var(--glass-hover)', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-disabled)', flex: 1 }}>{title}</Typography>
      <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{count} files · {fmtSize(bytes)}</Typography>
      {extra}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {selected.size > 0 && (
          <Box component="button" onClick={deleteSelected}
            sx={{ px: '12px', py: '5px', borderRadius: '7px', background: 'rgba(248,113,113,.12)', border: '1px solid rgba(248,113,113,.2)', color: '#f87171', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', '&:hover': { background: 'rgba(248,113,113,.22)' } }}>
            Delete selected ({selected.size})
          </Box>
        )}
        {files.some(f => !f.in_use) && selected.size === 0 && (
          <Box component="button" onClick={deleteUnused}
            sx={{ px: '12px', py: '5px', borderRadius: '7px', background: 'rgba(251,191,36,.08)', border: '1px solid rgba(251,191,36,.18)', color: '#fbbf24', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', '&:hover': { background: 'rgba(251,191,36,.16)' } }}>
            {t('screens.deleteUnused')}
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <Box component="button" onClick={() => sendCommand('clear_cache')}
          sx={{ px: '12px', py: '5px', borderRadius: '7px', background: 'rgba(248,113,113,.08)', border: '1px solid rgba(248,113,113,.16)', color: '#f87171', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', '&:hover': { background: 'rgba(248,113,113,.18)' } }}>
          {t('screens.clearCache')}
        </Box>
        <Box component="button" onClick={() => { fetchFiles(); fetchRequired(); }}
          sx={{ px: '10px', py: '5px', borderRadius: '7px', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', '&:hover': { background: 'var(--glass-hover)' } }}>
          ↻
        </Box>
      </Box>

      {/* Two-panel layout */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '12px', alignItems: 'flex-start' }}>

        {/* LEFT: Files on device */}
        <Box sx={{ flex: 1, minWidth: 0, borderRadius: '10px', border: '1px solid var(--glass-border)', overflow: 'hidden' }}>
          {panelHeader(
            'On Device',
            files.length,
            totalBytes,
            <Box component="input" type="checkbox"
              checked={files.length > 0 && selected.size === files.length}
              onChange={toggleAll}
              style={{ width: 13, height: 13, accentColor: 'var(--accent)', cursor: 'pointer' }} />,
          )}

          {loading && (
            <Box sx={{ py: '20px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>{t('common.loading')}</Typography>
            </Box>
          )}
          {!loading && files.length === 0 && (
            <Box sx={{ py: '28px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>{t('screens.noCachedFiles')}</Typography>
            </Box>
          )}
          {!loading && files.map((f, idx) => {
            const isSel = selected.has(f.storage_name);
            const isPending = pendingCmd.has(f.storage_name);
            return (
              <Box key={f.id} sx={{ display: 'grid', gridTemplateColumns: '24px 20px 1fr 68px 60px 30px', px: '10px', py: '7px', alignItems: 'center', gap: '4px', borderBottom: idx < files.length - 1 ? '1px solid var(--divider)' : 'none', background: isSel ? 'rgba(99,102,241,.06)' : 'transparent', opacity: isPending ? 0.45 : 1, transition: 'opacity .2s', '&:hover': { background: isSel ? 'rgba(99,102,241,.08)' : 'var(--row-hover)' } }}>
                <Box component="input" type="checkbox" checked={isSel} onChange={() => toggleSelect(f.storage_name)} style={{ width: 13, height: 13, accentColor: 'var(--accent)', cursor: 'pointer' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'var(--text-disabled)' }}>{fileIcon(f.mime_type)}</Box>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_name}</Typography>
                <Typography sx={{ fontSize: 11, color: 'var(--text-secondary)' }}>{fmtSize(f.size_bytes)}</Typography>
                <Box>
                  <Box sx={{ display: 'inline-flex', px: '5px', py: '2px', borderRadius: '5px', fontSize: 10, fontWeight: 600, ...(f.in_use ? { background: 'rgba(52,211,153,.1)', color: '#34d399', border: '1px solid rgba(52,211,153,.2)' } : { background: 'rgba(251,191,36,.08)', color: '#fbbf24', border: '1px solid rgba(251,191,36,.18)' }) }}>
                    {f.in_use ? 'In use' : 'Unused'}
                  </Box>
                </Box>
                <Box component="button" onClick={() => sendCommand('delete_file', { file_id: f.id, storage_name: f.storage_name }, f.storage_name)} disabled={isPending}
                  sx={{ width: 26, height: 26, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: isPending ? 'default' : 'pointer', color: 'var(--text-disabled)', '&:hover': { background: 'rgba(248,113,113,.12)', color: '#f87171' } }}>
                  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* RIGHT: Required / should be playing */}
        <Box sx={{ flex: 1, minWidth: 0, borderRadius: '10px', border: '1px solid var(--glass-border)', overflow: 'hidden' }}>
          {panelHeader('Should Play', allRequiredCount, reqTotalBytes)}

          {reqLoading && (
            <Box sx={{ py: '20px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>{t('common.loading')}</Typography>
            </Box>
          )}
          {!reqLoading && allRequiredCount === 0 && (
            <Box sx={{ py: '28px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>No required files</Typography>
            </Box>
          )}

          {/* Schedule content group */}
          {!reqLoading && scheduleLabel && scheduleFiles.length > 0 && (
            <>
              <Box sx={{ px: '10px', py: '5px', borderBottom: '1px solid var(--divider)', background: 'rgba(99,102,241,.06)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>Schedule content</Typography>
                <Typography sx={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{scheduleLabel}</Typography>
              </Box>
              {scheduleFiles.map((f) => {
                const cached = files.some(c => c.storage_name === f.storage_name);
                const alsoDefault = f.sources?.includes('default');
                return (
                  <Box key={`sched-${f.id}`} sx={{ display: 'grid', gridTemplateColumns: '20px 1fr 70px 58px', px: '10px', py: '6px', alignItems: 'center', gap: '4px', borderBottom: '1px solid var(--divider)', '&:hover': { background: 'var(--row-hover)' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'var(--text-disabled)' }}>{fileIcon(f.mime_type)}</Box>
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.file_name}>{f.file_name}</Typography>
                      {alsoDefault && <Typography sx={{ fontSize: 9, color: '#fbbf24', fontWeight: 600 }}>+ Default content</Typography>}
                    </Box>
                    <Typography sx={{ fontSize: 11, color: 'var(--text-secondary)' }}>{f.size_bytes ? fmtSize(f.size_bytes) : '—'}</Typography>
                    <Box>
                      <Box sx={{ display: 'inline-flex', px: '5px', py: '2px', borderRadius: '5px', fontSize: 10, fontWeight: 600, ...(cached ? { background: 'rgba(52,211,153,.1)', color: '#34d399', border: '1px solid rgba(52,211,153,.2)' } : { background: 'rgba(248,113,113,.08)', color: '#f87171', border: '1px solid rgba(248,113,113,.18)' }) }}>
                        {cached ? 'Cached' : 'Missing'}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </>
          )}

          {/* Default content group (files exclusive to default playlist, not in any schedule) */}
          {!reqLoading && (scheduleLabel ? defaultOnlyFiles.length > 0 : required.length > 0) && (
            <>
              {(playlistLabel || mediaLabel || scheduleLabel) && (
                <Box sx={{ px: '10px', py: '5px', borderBottom: '1px solid var(--divider)', background: 'rgba(251,191,36,.05)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>{scheduleLabel ? 'Default content' : playlistLabel ? 'Playlist' : 'Media'}</Typography>
                  <Typography sx={{ fontSize: 11, color: '#fbbf24', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{playlistLabel ?? mediaLabel ?? ''}</Typography>
                </Box>
              )}
              {(scheduleLabel ? defaultOnlyFiles : required).map((f, idx, arr) => {
                const cached = files.some(c => c.storage_name === f.storage_name);
                return (
                  <Box key={`def-${f.id}`} sx={{ display: 'grid', gridTemplateColumns: '20px 1fr 70px 58px', px: '10px', py: '7px', alignItems: 'center', gap: '4px', borderBottom: idx < arr.length - 1 ? '1px solid var(--divider)' : 'none', '&:hover': { background: 'var(--row-hover)' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'var(--text-disabled)' }}>{fileIcon(f.mime_type)}</Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.file_name}>{f.file_name}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'var(--text-secondary)' }}>{f.size_bytes ? fmtSize(f.size_bytes) : '—'}</Typography>
                    <Box>
                      <Box sx={{ display: 'inline-flex', px: '5px', py: '2px', borderRadius: '5px', fontSize: 10, fontWeight: 600, ...(cached ? { background: 'rgba(52,211,153,.1)', color: '#34d399', border: '1px solid rgba(52,211,153,.2)' } : { background: 'rgba(248,113,113,.08)', color: '#f87171', border: '1px solid rgba(248,113,113,.18)' }) }}>
                        {cached ? 'Cached' : 'Missing'}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      </Box>

      {/* Toast */}
      {toast && (
        <Box sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, px: '18px', py: '9px', borderRadius: '10px', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(20px)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, boxShadow: '0 8px 32px rgba(0,0,0,.3)' }}>
          {toast}
        </Box>
      )}
    </Box>
  );
};

// ── RightPanel ────────────────────────────────────────────────────────────────

interface RightPanelProps {
  screen: DsScreen;
  t: (k: string) => string;
}

const RightPanel: React.FC<RightPanelProps> = ({ screen, t }) => {
  const online = isOnline(screen.last_seen_at);
  const rel = fmtRelative(screen.last_seen_at);

  // ── Screenshot widget state ──────────────────────────────────────────────────
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [ssLoading, setSsLoading] = useState(false);
  const [ssWaiting, setSsWaiting] = useState(false);
  const [ssUpdated, setSsUpdated] = useState<Date | null>(null);
  const ssApiBase = axios.defaults.baseURL?.replace(/\/api\/?$/, '') ?? '';

  // Fetch current screenshot metadata on mount (no auto-refresh)
  useEffect(() => {
    axios.get(`/screens/devices/${screen.id}/screenshot`).then(({ data }) => {
      if (data?.url) {
        setScreenshotUrl(ssApiBase + data.url + '?t=' + Date.now());
        setSsUpdated(data.updated_at ? new Date(data.updated_at) : null);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen.id]);

  const requestScreenshot = useCallback(async () => {
    if (ssLoading || ssWaiting) return;
    setSsWaiting(true);
    // Send screenshot command to device
    try {
      await axios.post(`/screens/devices/${screen.id}/commands`, { command: 'screenshot' });
    } catch { setSsWaiting(false); return; }

    // Poll for new screenshot for up to 150s (device may be on 120s heartbeat interval)
    const startAt = Date.now();
    const prevUpdated = ssUpdated?.toISOString() ?? null;
    const poll = setInterval(async () => {
      try {
        const { data } = await axios.get(`/screens/devices/${screen.id}/screenshot`);
        if (data?.url && data.updated_at !== prevUpdated) {
          setScreenshotUrl(ssApiBase + data.url + '?t=' + Date.now());
          setSsUpdated(data.updated_at ? new Date(data.updated_at) : new Date());
          clearInterval(poll);
          setSsWaiting(false);
        }
      } catch { }
      if (Date.now() - startAt > 150_000) { clearInterval(poll); setSsWaiting(false); }
    }, 2000);
  }, [screen.id, ssLoading, ssWaiting, ssUpdated, ssApiBase]);
  // ─────────────────────────────────────────────────────────────────────────────

  const Row = ({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', py: '10px', borderBottom: '1px solid var(--divider)', '&:last-child': { borderBottom: 'none' } }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-disabled)' }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, color: 'var(--text-primary)', fontFamily: mono ? '"JetBrains Mono", monospace' : 'inherit', wordBreak: 'break-all' }}>{value}</Typography>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

      {/* Screenshot widget */}
      <Box sx={{ borderRadius: '12px', overflow: 'hidden', ...glassSx }}>
        {/* Preview */}
        <Box sx={{ position: 'relative', aspectRatio: '16/9', bgcolor: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {screenshotUrl ? (
            <Box component="img" src={screenshotUrl} alt="Screenshot" sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
          ) : (
            <Box sx={{ textAlign: 'center', color: 'var(--text-disabled)' }}>
              {ssLoading
                ? <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid var(--glass-border)', borderTopColor: 'var(--accent)', animation: 'spin .7s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } }, mx: 'auto' }} />
                : <><IcoCamera size={28} /><Typography sx={{ fontSize: 10, mt: '5px' }}>{online ? 'No screenshot yet' : 'Device offline'}</Typography></>}
            </Box>
          )}
          {ssLoading && screenshotUrl && (
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid rgba(255,255,255,.2)', borderTopColor: '#fff', animation: 'spin .7s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
            </Box>
          )}
        </Box>

        {/* Controls bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '12px', py: '8px', borderTop: '1px solid var(--divider)' }}>
          <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, mr: '8px' }}>
            {ssWaiting
              ? 'Waiting for screen update…'
              : ssUpdated
                ? `Screenshot: ${ssUpdated.toLocaleTimeString()}`
                : 'Screenshot'}
          </Typography>
          <Box component="button" onClick={requestScreenshot} disabled={ssWaiting || ssLoading} sx={{ display: 'flex', alignItems: 'center', gap: '4px', px: '8px', py: '4px', borderRadius: '7px', ...glassSx, color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: (ssWaiting || ssLoading) ? 'default' : 'pointer', opacity: (ssWaiting || ssLoading) ? .5 : 1, '&:hover': { color: 'var(--text-primary)' } }}>
            {ssWaiting
              ? <Box sx={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid rgba(255,255,255,.2)', borderTopColor: 'var(--accent)', animation: 'spin .7s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
              : <IcoRefresh />}
            Refresh
          </Box>
        </Box>
      </Box>

      {/* Status card */}
      <Box sx={{ borderRadius: '12px', p: '18px', ...glassSx }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '16px' }}>
          <Box sx={{ width: 44, height: 44, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: online ? 'linear-gradient(135deg, rgba(52,211,153,.25), rgba(52,211,153,.1))' : 'rgba(255,255,255,.05)', border: `1px solid ${online ? 'rgba(52,211,153,.3)' : 'var(--glass-border)'}`, color: online ? '#34d399' : 'var(--text-disabled)' }}>
            <IcoScreen size={20} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: online ? '#34d399' : 'rgba(255,255,255,.2)', boxShadow: online ? '0 0 6px rgba(52,211,153,.7)' : 'none' }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: online ? '#34d399' : 'var(--text-disabled)' }}>
                {online ? t('screens.online') : t('screens.offline')}
              </Typography>
            </Box>
            {rel && <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', mt: '2px' }}>{rel}</Typography>}
          </Box>
        </Box>

        <Row label={t('screens.deviceId')} value={`#${screen.device_id}`} mono />
        <Row label={t('screens.appVersion')} value={screen.app_version || '—'} mono />
        <Row label={t('screens.detail.ip')} value={screen.ip_address || '—'} mono />
        <Row label={t('screens.detail.since')} value={fmtDate(screen.created_at, '—')} />
        <Row label={t('screens.detail.configVer')} value={String(screen.config_version)} mono />
        <Row label={t('screens.detail.lastSync')} value={fmtDate(screen.last_tally_at, t('screens.detail.neverSynced'))} />

      </Box>

      {/* Storage bar */}
      {screen.storage_total_bytes != null && screen.storage_total_bytes > 0 && (
        <Box sx={{ borderRadius: '12px', p: '14px 18px', ...glassSx }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-disabled)', mb: '10px' }}>
            Storage
          </Typography>
          {(() => {
            const total = screen.storage_total_bytes ?? 0;
            const free  = screen.storage_free_bytes  ?? 0;
            const used  = total - free;
            const pct   = total > 0 ? Math.round((used / total) * 100) : 0;
            const fmtGb = (b: number) => b >= 1e9 ? `${(b / 1e9).toFixed(1)} GB` : `${(b / 1e6).toFixed(0)} MB`;
            return (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '6px' }}>
                  <Typography sx={{ fontSize: 11, color: 'var(--text-secondary)' }}>Used {fmtGb(used)}</Typography>
                  <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{fmtGb(free)} free</Typography>
                </Box>
                <Box sx={{ height: 6, borderRadius: '3px', bgcolor: 'rgba(255,255,255,.08)', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${pct}%`, borderRadius: '3px', bgcolor: pct > 85 ? '#f87171' : pct > 60 ? '#fbbf24' : 'var(--accent)', transition: 'width .3s' }} />
                </Box>
                <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', mt: '5px' }}>{pct}% used · {fmtGb(total)} total</Typography>
              </>
            );
          })()}
        </Box>
      )}

    </Box>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const TABS = ['settings', 'files', 'control'] as const;
type Tab = typeof TABS[number];

interface ScreenDetailProps { screenId?: number; onClose?: () => void; }

const ScreenDetailPage: React.FC<ScreenDetailProps> = ({ screenId: propScreenId, onClose }) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = String(propScreenId ?? paramId ?? '');
  const isPanel = propScreenId != null;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const auth = useSelector((s: RootState) => s.auth.value);
  const { setPublishConfig, setBreadcrumbs, setCommandsConfig, setSaveConfig } = usePageHeader();
  const isNarrow = useMediaQuery('(max-width:899px)');
  const goBack = () => { if (onClose) onClose(); else navigate('/screens'); };

  const [nameSaving, setNameSaving] = useState(false);
  const [nameSaved, setNameSaved]   = useState(false);

  const [screen, setScreen] = useState<DsScreen | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('settings');

  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);

  // ── Lifted form state ────────────────────────────────────────────────────────
  const [formName, setFormName] = useState('');
  const [formContentType, setFormContentType] = useState('off');
  const [formPlaylistId, setFormPlaylistId] = useState<number | null>(null);
  const [formMediaId, setFormMediaId] = useState<number | null>(null);
  const [formMediaScale, setFormMediaScale] = useState('fill');
  const [formScheduleId, setFormScheduleId] = useState<number | null>(null);
  const [formKioskMode, setFormKioskMode] = useState(false);
  const [formCrashLogEnabled, setFormCrashLogEnabled] = useState(true);
  const [formDisableStandby, setFormDisableStandby] = useState(false);
  const [formAutostart, setFormAutostart] = useState(true);
  const [formScreenOrientation, setFormScreenOrientation] = useState(0);
  const [formContentRotation, setFormContentRotation] = useState(0);
  const [formCacheTtlDays, setFormCacheTtlDays] = useState(0);

  // ── Delete device ─────────────────────────────────────────────────────────────
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!screen || deleting) return;
    setDeleting(true);
    try {
      await axios.delete(`/screens/devices/${screen.id}`);
      goBack();
    } catch { setDeleting(false); }
  };

  // ── Pending commands (for Settings queue block + topbar badge) ───────────────
  const [pendingCmds, setPendingCmds] = useState<ScreenCommand[]>([]);

  const fetchPendingCmds = useCallback(async () => {
    if (!screen) return;
    try {
      const { data } = await axios.get<ScreenCommand[]>(`/screens/devices/${screen.id}/commands`, { params: { limit: 50 } });
      setPendingCmds((Array.isArray(data) ? data : []).filter(c => c.status === 'pending' || c.status === 'sent'));
    } catch {}
  }, [screen?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!screen) return;
    fetchPendingCmds();
    const id = setInterval(fetchPendingCmds, 15000);
    return () => clearInterval(id);
  }, [fetchPendingCmds, screen?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Publish state ────────────────────────────────────────────────────────────
  const [publishing, setPublishing] = useState(false);
  const [publishState, setPublishState] = useState<'idle' | 'ok' | 'err'>('idle');
  const [bitDepthWarning, setBitDepthWarning] = useState<string[]>([]);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);
  // Persistent 10-bit warning — checked any time the selected playlist changes
  const [playlist10bit, setPlaylist10bit] = useState<{ fileName: string; mediaFileId: number }[]>([]);

  // Publish is active only when device-affecting fields differ from saved state
  const hasDeviceChanges = screen != null && (
    formContentType !== (screen.default_content_type || 'off') ||
    formPlaylistId  !== (screen.default_playlist_id  ?? null)  ||
    formMediaId     !== (screen.default_media_id     ?? null)  ||
    formMediaScale  !== (screen.default_media_scale  || 'fill') ||
    formScheduleId  !== (screen.schedule_id          ?? null)  ||
    formKioskMode      !== (screen.kiosk_mode      ?? false) ||
    formCrashLogEnabled !== (screen.crash_log_enabled ?? true) ||
    formDisableStandby !== (screen.disable_standby ?? false) ||
    formAutostart !== (screen.autostart_on_boot ?? true) ||
    formScreenOrientation !== (screen.screen_orientation ?? 0) ||
    formContentRotation   !== (screen.content_rotation   ?? 0) ||
    formCacheTtlDays          !== (screen.cache_ttl_days        ?? 30)
  );

  // Background 10-bit check: runs whenever the selected playlist changes
  useEffect(() => {
    if (formContentType !== 'playlist' || !formPlaylistId) { setPlaylist10bit([]); return; }
    let cancelled = false;
    axios.get<{ items: { mimeType: string; bitDepth?: number | null; fileName: string; mediaFileId: number }[] }>(`/playlists/${formPlaylistId}`)
      .then(({ data }) => {
        if (cancelled) return;
        const bad = (data.items ?? [])
          .filter(i => i.mimeType?.startsWith('video/') && i.bitDepth != null && (i.bitDepth as number) > 8)
          .map(i => ({ fileName: i.fileName, mediaFileId: i.mediaFileId }));
        // deduplicate by mediaFileId
        const seen = new Set<number>();
        setPlaylist10bit(bad.filter(f => { if (seen.has(f.mediaFileId)) return false; seen.add(f.mediaFileId); return true; }));
      })
      .catch(() => { if (!cancelled) setPlaylist10bit([]); });
    return () => { cancelled = true; };
  }, [formPlaylistId, formContentType]);

  const load = useCallback(async () => {
    try {
      const [{ data: sc }, { data: pls }, { data: mf }, { data: schs }] = await Promise.all([
        axios.get<DsScreen>(`/screens/devices/${id}`),
        axios.get<Playlist[]>('/playlists'),
        axios.get<MediaFile[]>('/media/files'),
        axios.get<Schedule[]>('/schedules'),
      ]);
      setScreen(sc);
      setPlaylists(Array.isArray(pls) ? pls : []);
      setMediaFiles(Array.isArray(mf) ? mf : []);
      setSchedules(Array.isArray(schs) ? schs : []);
    } catch { }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { if (auth) load(); }, [auth, load]);

  // Init form when screen loads (only when screen id changes)
  useEffect(() => {
    if (screen) {
      setFormName(screen.display_name || '');
      setFormContentType(screen.default_content_type || 'off');
      setFormPlaylistId(screen.default_playlist_id ?? null);
      setFormMediaId(screen.default_media_id ?? null);
      setFormMediaScale(screen.default_media_scale || 'fill');
      setFormScheduleId(screen.schedule_id ?? null);
      setFormKioskMode(screen.kiosk_mode ?? false);
      setFormCrashLogEnabled(screen.crash_log_enabled ?? true);
      setFormDisableStandby(screen.disable_standby ?? false);
      setFormAutostart(screen.autostart_on_boot ?? true);
      setFormScreenOrientation(screen.screen_orientation ?? 0);
      setFormContentRotation(screen.content_rotation ?? 0);
      setFormCacheTtlDays(screen.cache_ttl_days ?? 30);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen?.id]);

  // Breadcrumbs: Screens › <screen name> (skip in panel mode)
  useEffect(() => {
    if (!screen || isPanel) return;
    setBreadcrumbs([
      { label: t('screens.title'), onClick: () => goBack() },
      { label: screen.display_name || screen.device_id },
    ]);
    setCommandsConfig({ pendingCount: pendingCmds.length, onOpen: () => setTab('control') });
    return () => { setBreadcrumbs([]); setCommandsConfig(null); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen?.display_name, screen?.device_id, pendingCmds.length]);

  const saveName = async () => {
    if (!screen || nameSaving) return;
    const nameChanged = formName !== (screen.display_name || '');
    if (!nameChanged) return;

    setNameSaving(true);
    try {
      await axios.put(`/screens/devices/${screen.id}`, { display_name: formName });
      setScreen(prev => prev ? { ...prev, display_name: formName } : prev);
      if (!isPanel) setBreadcrumbs([
        { label: t('screens.title'), onClick: () => goBack() },
        { label: formName || screen.device_id },
      ]);
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2500);
    } catch {
      // ignore
    } finally {
      setNameSaving(false);
    }
  };

  // Save all settings: name + device config. Always sends reload command if device fields changed.
  const handleSaveAll = async () => {
    if (!screen || nameSaving) return;
    setNameSaving(true);
    setNameSaved(false);
    try {
      const nameChanged = formName !== (screen.display_name || '');
      const deviceChanged = (
        formContentType !== (screen.default_content_type || 'off') ||
        formPlaylistId  !== (screen.default_playlist_id  ?? null)  ||
        formMediaId     !== (screen.default_media_id     ?? null)  ||
        formMediaScale  !== (screen.default_media_scale  || 'fill') ||
        formScheduleId  !== (screen.schedule_id          ?? null)  ||
        formKioskMode      !== (screen.kiosk_mode      ?? false)      ||
        formCrashLogEnabled !== (screen.crash_log_enabled ?? true)    ||
        formDisableStandby !== (screen.disable_standby ?? false)      ||
        formAutostart !== (screen.autostart_on_boot ?? true)          ||
        formScreenOrientation !== (screen.screen_orientation ?? 0)    ||
        formContentRotation   !== (screen.content_rotation   ?? 0)    ||
        formCacheTtlDays       !== (screen.cache_ttl_days       ?? 30)
      );

      if (nameChanged) {
        await axios.put(`/screens/devices/${screen.id}`, { display_name: formName });
      }

      if (deviceChanged) {
        const { data: updated } = await axios.post<DsScreen>(`/screens/devices/${screen.id}/publish`, {
          default_content_type: formContentType,
          default_playlist_id: formContentType === 'playlist' ? formPlaylistId : null,
          default_media_id: formContentType === 'media' ? formMediaId : null,
          default_media_scale: formContentType === 'media' ? formMediaScale : null,
          schedule_id: formScheduleId,
          kiosk_mode: formKioskMode,
          crash_log_enabled: formCrashLogEnabled,
          disable_standby: formDisableStandby,
          autostart_on_boot: formAutostart,
          screen_orientation: formScreenOrientation,
          content_rotation: formContentRotation,
          cache_ttl_days: formCacheTtlDays,
        });
        setScreen(updated);
      } else if (nameChanged) {
        setScreen(prev => prev ? { ...prev, display_name: formName } : prev);
      }

      if (nameChanged && !isPanel) {
        setBreadcrumbs([
          { label: t('screens.title'), onClick: () => goBack() },
          { label: formName || screen.device_id },
        ]);
      }

      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2500);
    } catch { /* ignore */ } finally {
      setNameSaving(false);
    }
  };

  // Always-current ref so the mobile-save effect never captures a stale closure
  const handleSaveAllRef = useRef(handleSaveAll);
  handleSaveAllRef.current = handleSaveAll;

  // Mobile: Save lives in the topbar, the tabs row stays tabs only
  useEffect(() => {
    if (!isNarrow || tab !== 'settings' || !screen) { setSaveConfig(null); return; }
    setSaveConfig({
      dirty: true, enabled: !nameSaving, saving: nameSaving, saved: nameSaved,
      onSave: () => { handleSaveAllRef.current(); },
    });
    return () => setSaveConfig(null);
  }, [isNarrow, tab, nameSaving, nameSaved, screen, setSaveConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const doPublish = async () => {
    if (!screen || publishing) return;
    setBitDepthWarning([]);
    setPublishing(true);
    setPublishState('idle');
    try {
      const { data: updated } = await axios.post<DsScreen>(`/screens/devices/${screen.id}/publish`, {
        default_content_type: formContentType,
        default_playlist_id: formContentType === 'playlist' ? formPlaylistId : null,
        default_media_id: formContentType === 'media' ? formMediaId : null,
        default_media_scale: formContentType === 'media' ? formMediaScale : null,
        schedule_id: formScheduleId,
        kiosk_mode: formKioskMode,
        crash_log_enabled: formCrashLogEnabled,
        disable_standby: formDisableStandby,
        autostart_on_boot: formAutostart,
        screen_orientation: formScreenOrientation,
        content_rotation: formContentRotation,
        cache_ttl_days: formCacheTtlDays,
      });
      setScreen(updated);
      setPublishState('ok');
      setTimeout(() => setPublishState('idle'), 3000);
    } catch {
      setPublishState('err');
      setTimeout(() => setPublishState('idle'), 2500);
    } finally {
      setPublishing(false);
    }
  };

  const saveAndPublish = async () => {
    if (!screen || publishing) return;
    if (formContentType === 'playlist' && playlist10bit.length > 0) {
      setBitDepthWarning(playlist10bit.map(f => f.fileName));
      return;
    }
    if (!hasDeviceChanges) return;
    await doPublish();
  };

  // Register Publish button in Topbar via context; ref keeps saveAndPublish stable
  const saveAndPublishRef = useRef(saveAndPublish);
  saveAndPublishRef.current = saveAndPublish;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isPanel) return;
    setPublishConfig({
      onPublish: () => saveAndPublishRef.current(),
      disabled: publishing,
      publishState: publishing ? 'loading' : publishState,
    });
  }, [publishing, hasDeviceChanges, publishState, isPanel]);

  useEffect(() => () => { setPublishConfig(null); }, [setPublishConfig]);

  if (loading) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '10px' }}>
      <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid var(--glass-border)', borderTopColor: 'var(--accent)', animation: 'spin .7s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
      <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>Loading…</Typography>
    </Box>
  );

  if (!screen) return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 14, color: '#f87171' }}>Screen not found</Typography>
      <Box component="button" onClick={() => navigate('/screens')} sx={{ mt: '10px', display: 'inline-flex', alignItems: 'center', gap: '5px', px: '12px', py: '7px', borderRadius: '8px', cursor: 'pointer', ...glassSx, color: 'var(--text-secondary)', fontSize: 13 }}>
        <IcoBack /> {t('common.back')}
      </Box>
    </Box>
  );

  const tabLabel = (tab: Tab) => {
    if (tab === 'settings')  return <><IcoSettings size={13} /> {t('screens.detail.tabSettings')}</>;
    if (tab === 'files')     return <><span style={{ fontSize: 12 }}>◫</span> {t('screens.cachedFiles')}</>;
    return <><span style={{ fontSize: 12 }}>⚙</span> {t('screens.deviceControl')}</>;
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

      {/* ── Body 70/30 ── */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 300px' }, p: { xs: 0, md: '12px' }, gap: { xs: 0, md: '12px' } }}>

        {/* Left — a glass card on desktop, full-bleed on mobile */}
        <Box sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', ...glassSx, borderRadius: { xs: 0, md: '14px' }, border: { xs: 'none', md: '1px solid var(--glass-border)' } }}>

            {/* Status strip — desktop only; on mobile the topbar plus the tabs row are
                enough, and the delete action moved into Device control */}
            <Box sx={{ display: 'none', alignItems: 'center', gap: '10px', px: '14px', py: '9px', borderBottom: '1px solid var(--divider)', flexShrink: 0, bgcolor: 'var(--glass-hover)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: isOnline(screen.last_seen_at) ? '#34d399' : 'rgba(255,255,255,.2)', boxShadow: isOnline(screen.last_seen_at) ? '0 0 6px rgba(52,211,153,.7)' : 'none' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: isOnline(screen.last_seen_at) ? '#34d399' : 'var(--text-disabled)' }}>
                  {isOnline(screen.last_seen_at) ? t('screens.online') : t('screens.offline')}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>
                #{screen.device_id}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Box
                component="button"
                onClick={() => setConfirmDelete(true)}
                sx={{ display: 'flex', alignItems: 'center', gap: '5px', px: '8px', py: '4px', borderRadius: '7px', background: 'rgba(248,113,113,.08)', border: '1px solid rgba(248,113,113,.2)', color: '#f87171', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', '&:hover': { background: 'rgba(248,113,113,.18)' } }}
              >
                <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                {t('screens.deleteDevice')}
              </Box>
            </Box>

            {/* Card header: tabs + Save button */}
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px', px: '14px', py: '10px', borderBottom: '1px solid var(--divider)', overflowX: 'auto' }}>
              {TABS.map(t2 => (
                <Box key={t2} onClick={() => setTab(t2)} sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '12px', py: '5px', borderRadius: '7px', cursor: 'pointer', fontSize: 12, fontWeight: 600, bgcolor: tab === t2 ? 'rgba(255,255,255,.08)' : 'transparent', border: `1px solid ${tab === t2 ? 'var(--glass-border)' : 'transparent'}`, color: tab === t2 ? 'var(--text-primary)' : 'var(--text-disabled)', transition: 'all .12s', boxShadow: tab === t2 ? '0 1px 3px rgba(0,0,0,.2)' : 'none' }}>
                  {tabLabel(t2)}
                </Box>
              ))}
              {tab === 'settings' && (
                <Box component="button" onClick={handleSaveAll} disabled={nameSaving}
                  sx={{ ml: 'auto', display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: '5px', px: '12px', py: '5px', borderRadius: '7px',
                    cursor: nameSaving ? 'default' : 'pointer', fontSize: 12, fontWeight: 600, border: 'none', transition: 'all .15s',
                    bgcolor: nameSaved ? 'rgba(52,211,153,.15)' : 'var(--accent)',
                    color: nameSaved ? '#34d399' : '#fff',
                    opacity: nameSaving ? .7 : 1,
                  }}>
                  {nameSaved ? '✓ Saved' : nameSaving ? '…' : t('common.save')}
                </Box>
              )}
            </Box>

            {/* Card body: scrollable tab content */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: '20px' }}>
              {tab === 'settings' && (
                <SettingsTab
                  screen={screen} playlists={playlists} mediaFiles={mediaFiles} schedules={schedules} t={t}
                  name={formName} setName={setFormName}
                  contentType={formContentType} setContentType={setFormContentType}
                  playlistId={formPlaylistId} setPlaylistId={setFormPlaylistId}
                  mediaId={formMediaId} setMediaId={setFormMediaId}
                  mediaScale={formMediaScale} setMediaScale={setFormMediaScale}
                  scheduleId={formScheduleId} setScheduleId={setFormScheduleId}
                  kioskMode={formKioskMode} setKioskMode={setFormKioskMode}
                  crashLogEnabled={formCrashLogEnabled} setCrashLogEnabled={setFormCrashLogEnabled}
                  disableStandby={formDisableStandby} setDisableStandby={setFormDisableStandby}
                  autostart={formAutostart} setAutostart={setFormAutostart}
                  screenOrientation={formScreenOrientation} setScreenOrientation={setFormScreenOrientation}
                  contentRotation={formContentRotation} setContentRotation={setFormContentRotation}
                  cacheTtlDays={formCacheTtlDays} setCacheTtlDays={setFormCacheTtlDays}
                  playlist10bit={playlist10bit}
                />
              )}
              {tab === 'files'    && <FilesOnDeviceTab screen={screen} playlists={playlists} schedules={schedules} mediaFiles={mediaFiles} t={t} />}
              {tab === 'control'  && <DeviceControlTab screen={screen} pendingCmds={pendingCmds} t={t} onDelete={() => setConfirmDelete(true)} />}
            </Box>
          </Box>
        </Box>

        {/* Right 300px — status panel (desktop only) */}
        <Box sx={{ overflowY: 'auto', p: '16px', display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: '12px' }}>
          <RightPanel screen={screen} t={t} />

          {/* Assigned content card */}
          {(screen.default_content_type && screen.default_content_type !== 'off') && (
            <Box sx={{ borderRadius: '12px', p: '16px', ...glassSx }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-disabled)', mb: '12px' }}>
                Assigned Content
              </Typography>
              {screen.default_content_type === 'playlist' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '6px' }}>
                  <Box sx={{ width: 26, height: 26, borderRadius: '6px', bgcolor: 'rgba(99,102,241,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 12 }}>▤</span>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Playlist</Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>
                      {playlists.find(p => p.id === screen.default_playlist_id)?.name || '—'}
                    </Typography>
                  </Box>
                </Box>
              )}
              {screen.default_content_type === 'media' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '6px' }}>
                  <Box sx={{ width: 26, height: 26, borderRadius: '6px', bgcolor: 'rgba(52,211,153,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <IcoPlay size={12} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Single file</Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>
                      {mediaFiles.find(f => f.id === screen.default_media_id)?.original_name || '—'}
                    </Typography>
                  </Box>
                </Box>
              )}
              {screen.schedule_id && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Box sx={{ width: 26, height: 26, borderRadius: '6px', bgcolor: 'rgba(168,85,247,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 12 }}>📅</Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Schedule</Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>
                      {schedules.find(s => s.id === screen.schedule_id)?.name || '—'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Delete device */}
          <Box sx={{ borderRadius: '12px', p: '16px', border: '1px solid rgba(248,113,113,.2)', bgcolor: 'rgba(248,113,113,.04)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: '#f87171', mb: '10px' }}>
              {t('screens.dangerZone')}
            </Typography>
            {!confirmDelete ? (
              <Box
                component="button"
                onClick={() => setConfirmDelete(true)}
                sx={{ width: '100%', px: '12px', py: '8px', borderRadius: '8px', border: '1px solid rgba(248,113,113,.3)', bgcolor: 'transparent', color: '#f87171', fontSize: 12, fontWeight: 600, cursor: 'pointer', textAlign: 'left', transition: 'background .15s', '&:hover': { bgcolor: 'rgba(248,113,113,.08)' } }}
              >
                {t('screens.deleteDevice')}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {t('screens.deleteDeviceConfirm')}
                </Typography>
                <Box sx={{ display: 'flex', gap: '8px' }}>
                  <Box
                    component="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    sx={{ flex: 1, px: '10px', py: '7px', borderRadius: '8px', border: 'none', bgcolor: '#f87171', color: '#fff', fontSize: 12, fontWeight: 700, cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.7 : 1 }}
                  >
                    {deleting ? '...' : t('common.delete')}
                  </Box>
                  <Box
                    component="button"
                    onClick={() => setConfirmDelete(false)}
                    sx={{ flex: 1, px: '10px', py: '7px', borderRadius: '8px', border: '1px solid var(--glass-border)', bgcolor: 'var(--glass-bg)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    {t('common.cancel')}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── 10-bit warning modal ── */}
      <Dialog
        open={bitDepthWarning.length > 0}
        onClose={() => { if (!converting) { setBitDepthWarning([]); setConvertError(null); } }}
        slotProps={{ paper: { sx: { background: 'var(--glass-bg)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid var(--glass-border)', borderRadius: '14px', minWidth: 380, maxWidth: '95vw' } } }}
      >
        <DialogContent sx={{ pt: '24px', pb: '8px', px: '24px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '14px' }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'rgba(255,170,0,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#ffaa00', fontSize: 16, fontWeight: 700 }}>!</Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
              {t('screens.bitDepthWarnTitle')}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: 'var(--text-secondary)', mb: '12px', lineHeight: 1.5 }}>
            {t('screens.bitDepthWarnDesc')}
          </Typography>
          <Box sx={{ borderRadius: '8px', bgcolor: 'rgba(255,170,0,.07)', border: '1px solid rgba(255,170,0,.2)', p: '10px 12px', mb: '12px' }}>
            {bitDepthWarning.map((name, i) => (
              <Typography key={i} sx={{ fontSize: 12, color: 'var(--text-primary)', fontFamily: '"JetBrains Mono", monospace', py: '2px' }}>
                {name}
              </Typography>
            ))}
          </Box>
          <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {t('screens.bitDepthConvertDesc')}
          </Typography>
          {convertError && (
            <Typography sx={{ fontSize: 12, color: '#f87171', mt: '8px', fontFamily: '"JetBrains Mono", monospace' }}>
              {convertError}
            </Typography>
          )}
          {converting && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '10px' }}>
              <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(99,102,241,.3)', borderTopColor: 'var(--accent)', animation: 'spin .7s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } }, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('screens.bitDepthConverting')}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: '24px', pb: '20px', pt: '8px', gap: '8px', flexWrap: 'wrap' }}>
          <Button
            onClick={() => { setBitDepthWarning([]); setConvertError(null); }}
            disabled={converting}
            sx={{ fontSize: 13, color: 'var(--text-secondary)', textTransform: 'none', px: '14px' }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => { setBitDepthWarning([]); setConvertError(null); doPublish(); }}
            disabled={converting}
            sx={{ fontSize: 13, fontWeight: 600, textTransform: 'none', bgcolor: 'rgba(255,170,0,.1)', color: '#ffaa00', border: '1px solid rgba(255,170,0,.25)', borderRadius: '8px', px: '14px', '&:hover': { bgcolor: 'rgba(255,170,0,.2)' } }}
          >
            {t('screens.bitDepthPublishAnyway')}
          </Button>
          <Button
            onClick={async () => {
              setConverting(true);
              setConvertError(null);
              try {
                // Convert each 10-bit file; check success flag
                const results = await Promise.all(
                  playlist10bit.map(f =>
                    axios.post<{ success: boolean; message?: string }>(`/media/files/${f.mediaFileId}/convert-to-8bit`)
                      .then(r => ({ ...r.data, mediaFileId: f.mediaFileId, fileName: f.fileName }))
                  )
                );
                const failed = results.filter(r => !r.success);
                if (failed.length > 0) {
                  setConvertError(failed.map(r => `${r.fileName}: ${r.message ?? 'error'}`).join('; '));
                  return;
                }
                // Re-fetch playlist to verify all files are now 8-bit
                const { data } = await axios.get<{ items: { mimeType: string; bitDepth?: number | null; fileName: string; mediaFileId: number }[] }>(`/playlists/${formPlaylistId}`);
                const still10bit = (data.items ?? [])
                  .filter(i => i.mimeType?.startsWith('video/') && i.bitDepth != null && (i.bitDepth as number) > 8)
                  .map(i => ({ fileName: i.fileName, mediaFileId: i.mediaFileId }));
                setPlaylist10bit(still10bit);
                setBitDepthWarning([]);
                if (still10bit.length === 0) {
                  await doPublish();
                }
              } catch (e: any) {
                setConvertError(e?.response?.data?.message ?? 'Conversion failed');
              } finally {
                setConverting(false);
              }
            }}
            disabled={converting}
            sx={{ fontSize: 13, fontWeight: 600, textTransform: 'none', bgcolor: 'rgba(99,102,241,.15)', color: 'var(--accent-light)', border: '1px solid rgba(99,102,241,.3)', borderRadius: '8px', px: '16px', '&:hover': { bgcolor: 'rgba(99,102,241,.25)' } }}
          >
            {converting ? '…' : t('screens.bitDepthConvert')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScreenDetailPage;
